import dedent from 'ts-dedent'
import { MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class Area extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.area',
        group: 'Spatial Operations',
        title: 'ST Area',
        description: `
      This component creates a new table with an additional column containing the area
      of the geographies in the input table. This only applies to polygons.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source polygons table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const convert = params.units === 'Meters' ? '' : ` / (${MILES_TO_M}*${MILES_TO_M})`
    switch (connection.type) {
      case Provider.Redshift:
      case Provider.Postgres:
        // converted to geography that always give back sqm to avoid to
        // evaluate CRS
        return dedent`SELECT *,
                  (
                    ST_AREA(${params.geo}::geography)
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        // converted to geography that always give back sqm to avoid to
        // evaluate CRS
        return dedent`SELECT *,
                  (
                    ST_AREA(TO_GEOGRAPHY(${params.geo}))
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.BigQuery:
        return dedent`SELECT *,
                  (
                    ST_AREA(${params.geo})
                  ) ${convert}
                  AS
                   ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40) */
                  *,
                  (
                    ST_AREA(${connection.processInputGeogColumn(params.geo as string)})
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.Oracle: {
        const geoColumn = params.geo as string
        const areaColumn = `${params.geo}_area`
        // Oracle SDO_GEOM.SDO_AREA returns square meters by default
        // Convert to square miles if needed: divide by (1609.344^2)
        const oracleConvert = params.units === 'Meters' ? '' : ` / ${MILES_TO_M * MILES_TO_M}`
        return dedent`SELECT t.*,
                (
                  SDO_GEOM.SDO_AREA(t.${geoColumn}, 0.005, 'unit=SQ_M')
                ) ${oracleConvert}
                AS ${areaColumn}
              FROM ${connection.quoteName(params.source as string)} t`
      }
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geo}_area`,
      type: ColumnDataType.Number
    })
  }
}

export { Area }
